import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TextField,
  InputAdornment,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  formatCurrency,
  formatSignedCurrency,
  formatSignedPercent,
} from "../../../../constants/investmentLeague";

const SearchIcon = ({ color = "currentColor" }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const WarningIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const ReinvestTable = ({
  units = [],
  allocations = {},
  onAllocationChange,
  investorUnit = "",
  disabled = false,
  previousQuarter = "Q2",
  upcomingQuarter = "Q3",
}) => {
  const theme = useTheme();
  const t = theme.custom;
  const isDark = theme.palette.mode === "dark";
  const [searchTerm, setSearchTerm] = useState("");

  // Normalize investor unit string to check exclusion
  const cleanInvestorUnit = (investorUnit || "").trim().toLowerCase();

  // Exclude current investor unit from recipient list
  const filteredRecipientUnits = useMemo(() => {
    return units.filter((unit) => {
      const uName = (unit.buUnit || unit.unitName || "").toLowerCase();
      const uCode = (unit.unitCode || "").toLowerCase();
      if (!cleanInvestorUnit) return true;
      return !cleanInvestorUnit.includes(uCode) && !cleanInvestorUnit.includes(uName);
    });
  }, [units, cleanInvestorUnit]);

  // Apply search query
  const displayedUnits = useMemo(() => {
    if (!searchTerm.trim()) return filteredRecipientUnits;
    const query = searchTerm.toLowerCase();
    return filteredRecipientUnits.filter(
      (u) =>
        (u.buUnit || "").toLowerCase().includes(query) ||
        (u.unitCode || "").toLowerCase().includes(query) ||
        (u.type || "").toLowerCase().includes(query)
    );
  }, [filteredRecipientUnits, searchTerm]);

  const handleInputChange = (unitKey, rawValue) => {
    // Only allow positive integers or 0
    const digitsOnly = rawValue.replace(/[^0-9]/g, "");
    const numericValue = digitsOnly === "" ? 0 : parseInt(digitsOnly, 10);
    onAllocationChange(unitKey, numericValue);
  };

  return (
    <Box
      sx={{
        backgroundColor: t.surface,
        border: `1px solid ${t.border2}`,
        borderRadius: "12px",
        p: { xs: 2, sm: 2.5 },
        boxShadow: isDark ? "0 4px 16px rgba(0, 0, 0, 0.25)" : "0 2px 8px rgba(3, 81, 77, 0.06)",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* Table Header: Title + Search */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          mb: 2.5,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.05rem",
              color: t.text,
              letterSpacing: "-0.01em",
            }}
          >
            Allocate Investments for {upcomingQuarter}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.78rem",
              color: t.text2,
            }}
          >
            Enter the amount you want to invest in each unit. Total amount should not exceed your available credit.
          </Typography>
        </Box>

        {/* Search Field */}
        <TextField
          size="small"
          placeholder="Search MACs or Groups..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color={t.text3} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: { xs: "100%", sm: "240px" },
            "& .MuiOutlinedInput-root": {
              backgroundColor: isDark ? "rgba(30, 41, 59, 0.6)" : t.surface2,
              borderRadius: "8px",
              fontSize: "0.82rem",
              color: t.text,
              height: "36px",
              "& fieldset": {
                borderColor: t.border2,
              },
              "&:hover fieldset": {
                borderColor: t.border3,
              },
              "&.Mui-focused fieldset": {
                borderColor: isDark ? "#10B981" : t.accent,
              },
            },
          }}
        />
      </Box>

      {/* Table Container */}
      <TableContainer
        sx={{
          flex: 1,
          overflow: "auto",
          maxHeight: { xs: "500px", md: "calc(100vh - 380px)" },
          "&::-webkit-scrollbar": {
            width: "6px",
            height: "6px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(3, 81, 77, 0.15)",
            borderRadius: "3px",
          },
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  backgroundColor: isDark ? "#0d1527" : t.surface2,
                  borderBottom: `1px solid ${t.border2}`,
                  color: t.text3,
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  py: 1.25,
                  whiteSpace: "nowrap",
                },
              }}
            >
              <TableCell>UNIT</TableCell>
              <TableCell>TYPE</TableCell>
              <TableCell align="right">PREVIOUS QUARTER INVESTED</TableCell>
              <TableCell align="right">PREVIOUS QUARTER RETURN</TableCell>
              <TableCell align="right">PREVIOUS QUARTER ROI</TableCell>
              <TableCell align="right">CUMULATIVE VARIANCE</TableCell>
              <TableCell align="center" sx={{ minWidth: "150px" }}>INVEST IN {upcomingQuarter}</TableCell>
              <TableCell align="center">STATUS</TableCell>
              <TableCell align="left">JACKPOT REQUIREMENT</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {displayedUnits.map((row) => {
              const unitKey = row.buUnit || row.unitName || row.unitCode;
              const allocatedValue = allocations[unitKey] !== undefined
                ? allocations[unitKey]
                : (row.currentQuarterInvestment || row.currentAllocation || 0);

              const isInvested = allocatedValue > 0;
              const isRequired = row.requiredForJackpot || row.investedInPreviousQuarter || (row.previousQuarterInvestment > 0);
              const isGroup = row.type === "Group";

              // Previous quarter financial metrics
              const prevInvested = row.previousQuarterInvestment ?? row.q2Invested ?? 0;
              const prevReturn = row.previousQuarterReturn ?? row.q2Return ?? 0;
              const prevRoi = row.previousQuarterRoi != null ? row.previousQuarterRoi : row.q2Roi;

              // Formatting variance
              const cumVar = row.cumulativeVariance;
              const hasVar = cumVar != null && !isNaN(cumVar);
              const isVarPos = hasVar && cumVar > 0;
              const isVarNeg = hasVar && cumVar < 0;

              return (
                <TableRow
                  key={unitKey}
                  hover
                  sx={{
                    "&:hover": {
                      backgroundColor: isDark ? "rgba(255, 255, 255, 0.03)" : t.surface2,
                    },
                    "& td": {
                      borderBottom: `1px solid ${t.border}`,
                      py: 1.2,
                      fontSize: "0.82rem",
                      color: t.text,
                    },
                  }}
                >
                  {/* Unit Name */}
                  <TableCell sx={{ fontWeight: 600, color: t.text }}>
                    {row.buUnit || row.unitName}
                  </TableCell>

                  {/* Unit Type Badge */}
                  <TableCell>
                    <Chip
                      label={row.type}
                      size="small"
                      sx={{
                        height: "20px",
                        fontSize: "0.68rem",
                        fontWeight: 600,
                        backgroundColor: isGroup
                          ? (isDark ? "rgba(147, 51, 234, 0.2)" : "rgba(147, 51, 234, 0.12)")
                          : (isDark ? "rgba(37, 99, 235, 0.2)" : "rgba(37, 99, 235, 0.12)"),
                        color: isGroup
                          ? (isDark ? "#C084FC" : "#7E22CE")
                          : (isDark ? "#60A5FA" : "#1D4ED8"),
                        border: `1px solid ${isGroup
                          ? (isDark ? "rgba(147, 51, 234, 0.4)" : "rgba(147, 51, 234, 0.3)")
                          : (isDark ? "rgba(37, 99, 235, 0.4)" : "rgba(37, 99, 235, 0.3)")}`,
                        borderRadius: "4px",
                      }}
                    />
                  </TableCell>

                  {/* Previous Quarter Invested */}
                  <TableCell align="right" sx={{ color: t.text }}>
                    {formatCurrency(prevInvested)}
                  </TableCell>

                  {/* Previous Quarter Return */}
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      color: prevReturn >= 0 ? (isDark ? "#10B981" : "#2D8A5B") : (isDark ? "#EF4444" : "#DC2626"),
                    }}
                  >
                    {formatSignedCurrency(prevReturn)}
                  </TableCell>

                  {/* Previous Quarter ROI */}
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      color: prevRoi == null
                        ? t.text3
                        : prevRoi >= 0
                        ? (isDark ? "#10B981" : "#2D8A5B")
                        : (isDark ? "#EF4444" : "#DC2626"),
                    }}
                  >
                    {prevRoi != null ? formatSignedPercent(prevRoi) : "-"}
                  </TableCell>

                  {/* Cumulative Variance */}
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      color: isVarPos
                        ? (isDark ? "#10B981" : "#2D8A5B")
                        : isVarNeg
                        ? (isDark ? "#EF4444" : "#DC2626")
                        : t.text3,
                    }}
                  >
                    {hasVar ? formatSignedPercent(cumVar) : "-"}
                  </TableCell>

                  {/* Invest in Upcoming Quarter Input */}
                  <TableCell align="center">
                    <TextField
                      size="small"
                      disabled={disabled}
                      value={allocatedValue === 0 ? "" : allocatedValue}
                      onChange={(e) => handleInputChange(unitKey, e.target.value)}
                      placeholder="0"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography sx={{ color: t.text3, fontSize: "0.82rem" }}>
                              $
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        width: "120px",
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: isDark ? "rgba(15, 23, 42, 0.7)" : t.surface2,
                          borderRadius: "6px",
                          fontSize: "0.82rem",
                          color: t.text,
                          height: "32px",
                          "& fieldset": {
                            borderColor: allocatedValue > 0
                              ? (isDark ? "rgba(16, 185, 129, 0.5)" : "rgba(45, 138, 91, 0.5)")
                              : t.border2,
                          },
                          "&:hover fieldset": {
                            borderColor: t.border3,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: isDark ? "#10B981" : t.accent,
                          },
                          "&.Mui-disabled": {
                            backgroundColor: isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(3, 81, 77, 0.04)",
                          },
                        },
                        "& input": {
                          textAlign: "right",
                          py: 0.5,
                        },
                      }}
                    />
                  </TableCell>

                  {/* Status Badge */}
                  <TableCell align="center">
                    <Chip
                      label={isInvested ? "Invested" : (isRequired ? "At Risk" : "Not Invested")}
                      size="small"
                      sx={{
                        height: "20px",
                        fontSize: "0.68rem",
                        fontWeight: 600,
                        backgroundColor: isInvested
                          ? (isDark ? "rgba(16, 185, 129, 0.15)" : "rgba(45, 138, 91, 0.10)")
                          : isRequired
                          ? (isDark ? "rgba(245, 158, 11, 0.15)" : "rgba(245, 158, 11, 0.10)")
                          : (isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(3, 81, 77, 0.05)"),
                        color: isInvested
                          ? (isDark ? "#10B981" : "#2D8A5B")
                          : isRequired
                          ? (isDark ? "#F59E0B" : "#B45309")
                          : t.text3,
                        border: `1px solid ${
                          isInvested
                            ? (isDark ? "rgba(16, 185, 129, 0.3)" : "rgba(45, 138, 91, 0.25)")
                            : isRequired
                            ? (isDark ? "rgba(245, 158, 11, 0.3)" : "rgba(245, 158, 11, 0.25)")
                            : t.border
                        }`,
                        borderRadius: "10px",
                      }}
                    />
                  </TableCell>

                  {/* Jackpot Requirement Indicator */}
                  <TableCell align="left">
                    {isRequired ? (
                      isInvested ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <CheckIcon />
                          <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#10B981" : "#2D8A5B", fontWeight: 500 }}>
                            Previously invested
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <WarningIcon />
                          <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#F59E0B" : "#B45309", fontWeight: 600 }}>
                            Missing for Jackpot
                          </Typography>
                        </Box>
                      )
                    ) : (
                      <Typography sx={{ fontSize: "0.75rem", color: t.text3 }}>
                        Not Previously Invested
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ReinvestTable;

